/** 在编辑器内保存 / 恢复 DOM 选区（用于 // 唤起 AI 后在光标处插入） */
export type EditorCursorAnchor = {
  range: Range
}

export function captureEditorCursor(container: HTMLElement | null): EditorCursorAnchor | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0 || !container) return null
  const range = sel.getRangeAt(0)
  if (!container.contains(range.commonAncestorContainer)) return null
  return { range: range.cloneRange() }
}

export function restoreEditorCursor(anchor: EditorCursorAnchor | null, container: HTMLElement | null): boolean {
  if (!anchor || !container) return false
  const { range } = anchor
  if (!container.contains(range.startContainer)) return false
  const sel = window.getSelection()
  if (!sel) return false
  sel.removeAllRanges()
  sel.addRange(range)
  return true
}

export function getTextBeforeAnchor(anchor: EditorCursorAnchor | null, container: HTMLElement | null): string {
  if (!anchor || !container) return ''
  try {
    const range = document.createRange()
    range.selectNodeContents(container)
    range.setEnd(anchor.range.startContainer, anchor.range.startOffset)
    return range.toString().replace(/\u200b/g, '')
  } catch {
    return ''
  }
}

export function getHtmlBeforeAnchor(anchor: EditorCursorAnchor | null, container: HTMLElement | null): string {
  if (!anchor || !container) return ''
  try {
    const range = document.createRange()
    range.selectNodeContents(container)
    range.setEnd(anchor.range.startContainer, anchor.range.startOffset)
    const wrap = document.createElement('div')
    wrap.appendChild(range.cloneContents())
    return wrap.innerHTML.replace(/\u200b/g, '')
  } catch {
    return ''
  }
}

export function getTextAfterAnchor(anchor: EditorCursorAnchor | null, container: HTMLElement | null): string {
  if (!anchor || !container) return ''
  try {
    const range = document.createRange()
    range.selectNodeContents(container)
    range.setStart(anchor.range.startContainer, anchor.range.startOffset)
    return range.toString().replace(/\u200b/g, '')
  } catch {
    return ''
  }
}

export function getHtmlAfterAnchor(anchor: EditorCursorAnchor | null, container: HTMLElement | null): string {
  if (!anchor || !container) return ''
  try {
    const range = document.createRange()
    range.selectNodeContents(container)
    range.setStart(anchor.range.startContainer, anchor.range.startOffset)
    const wrap = document.createElement('div')
    wrap.appendChild(range.cloneContents())
    return wrap.innerHTML.replace(/\u200b/g, '')
  } catch {
    return ''
  }
}

export function getAnchorClientRect(anchor: EditorCursorAnchor | null): DOMRect | null {
  if (!anchor) return null
  try {
    const range = anchor.range.cloneRange()
    const rects = range.getClientRects()
    if (rects.length > 0) {
      let top = Infinity
      let left = Infinity
      let bottom = -Infinity
      let right = -Infinity
      for (let i = 0; i < rects.length; i += 1) {
        const r = rects[i]
        if (r.width === 0 && r.height === 0) continue
        top = Math.min(top, r.top)
        left = Math.min(left, r.left)
        bottom = Math.max(bottom, r.bottom)
        right = Math.max(right, r.right)
      }
      if (top !== Infinity) {
        return new DOMRect(left, top, right - left, bottom - top)
      }
    }
    const rect = range.getBoundingClientRect()
    if (rect.width || rect.height) return rect

    const probe = range.cloneRange()
    if (!probe.collapsed) probe.collapse(false)
    const marker = document.createElement('span')
    marker.textContent = '\u200b'
    probe.insertNode(marker)
    const r = marker.getBoundingClientRect()
    marker.remove()
    return r.width || r.height ? r : null
  } catch {
    return null
  }
}

export function getCaretClientRect(container: HTMLElement | null): DOMRect | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  if (container && !container.contains(range.commonAncestorContainer)) return null

  const rects = range.getClientRects()
  if (rects.length > 0) {
    const last = rects[rects.length - 1]
    if (last.width || last.height) return last
  }

  const rect = range.getBoundingClientRect()
  if (rect.width || rect.height) return rect

  const clone = range.cloneRange()
  clone.collapse(true)
  const marker = document.createElement('span')
  marker.textContent = '\u200b'
  clone.insertNode(marker)
  const r = marker.getBoundingClientRect()
  marker.remove()
  return r.width || r.height ? r : null
}

function normalizePlainText(text: string): string {
  return text.replace(/\u200b/g, '').replace(/\s+/g, ' ').trim()
}

function plainLenFromMarkdownPrefix(markdownPrefix: string): number {
  return normalizePlainText(markdownSliceToPlain(markdownPrefix)).length
}

/** 将 DOM 可见文本边界映射到 Markdown 源文分割点 */
export function resolveMarkdownCursorSplit(
  markdown: string,
  plainBefore: string,
  plainAfter: string,
): { before: string; after: string } {
  const full = markdown || ''
  const targetBeforeLen = normalizePlainText(plainBefore).length
  const targetAfterLen = normalizePlainText(plainAfter).length

  if (!full.trim()) {
    return {
      before: plainBefore.trimEnd(),
      after: plainAfter.trimStart(),
    }
  }

  let split = full.length
  if (targetBeforeLen > 0) {
    let lo = 0
    let hi = full.length
    let best = 0
    let bestDiff = Infinity
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2)
      const len = plainLenFromMarkdownPrefix(full.slice(0, mid))
      const diff = Math.abs(len - targetBeforeLen)
      if (diff < bestDiff) {
        bestDiff = diff
        best = mid
      }
      if (len < targetBeforeLen) lo = mid + 1
      else hi = mid - 1
    }
    split = best
  } else if (targetAfterLen > 0) {
    let lo = 0
    let hi = full.length
    let best = full.length
    let bestDiff = Infinity
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2)
      const len = plainLenFromMarkdownPrefix(full.slice(mid))
      const diff = Math.abs(len - targetAfterLen)
      if (diff < bestDiff) {
        bestDiff = diff
        best = mid
      }
      if (len > targetAfterLen) lo = mid + 1
      else hi = mid - 1
    }
    split = best
  }

  return {
    before: full.slice(0, split).trimEnd(),
    after: full.slice(split).trimStart(),
  }
}

/** 用光标前可见文本反推 Markdown 源文前缀 */
export function sliceMarkdownBeforePlain(markdown: string, plainBefore: string): string {
  return resolveMarkdownCursorSplit(markdown, plainBefore, '').before
}

function markdownSliceToPlain(slice: string): string {
  return slice
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** 用光标后可见文本反推 Markdown 源文后缀 */
export function sliceMarkdownAfterPlain(markdown: string, plainAfter: string): string {
  return resolveMarkdownCursorSplit(markdown, '', plainAfter).after
}

export function buildCursorContextExcerpt(before: string, after: string, maxTotal = 12000): string {
  const beforeText = before.trim()
  const afterText = after.trim()
  if (!beforeText && !afterText) return ''

  const beforeBudget = Math.floor(maxTotal * 0.75)
  const afterBudget = maxTotal - beforeBudget
  const parts: string[] = []

  if (beforeText) {
    parts.push(`【光标之前（含当前列表/段落上下文，Markdown 源文）】\n${beforeText.slice(-beforeBudget)}`)
    const incompleteList = beforeText.match(/(?:^|\n)(\d+)\.\s*([^\n]*)$/)
    if (incompleteList) {
      const num = incompleteList[1]
      const partial = incompleteList[2]?.trim()
      parts.push(
        `【光标处未完成条目】当前在第 ${num} 条${partial ? `，已输入「${partial}」` : '，仅有编号'}；请参照前面各条目的格式与语气补全本条。`,
      )
    }
  }
  if (afterText) {
    parts.push(`【光标之后（Markdown 源文）】\n${afterText.slice(0, afterBudget)}`)
  }
  parts.push(
    '【插入说明】输出将插入光标处；若在有序列表中，只生成当前条目的续写内容（含编号与正文），勿重复前面条目，勿输出整篇文档。',
  )
  return parts.join('\n\n')
}

export function buildCursorModeUserHint(userMsg: string, before: string): string {
  const incompleteList = before.trim().match(/(?:^|\n)(\d+)\.\s*([^\n]*)$/)
  if (incompleteList) {
    const num = Number(incompleteList[1])
    const prevHint = num > 1 ? `第 1 至 ${num - 1} 条` : '前文'
    return `${userMsg}（光标位于第 ${num} 条有序列表未完成处；请严格参照【光标之前】中${prevHint}的写法、粒度与 Markdown 格式，补全当前条目；只输出待插入片段，不要重复前文，不要写后续条目。）`
  }
  return `${userMsg}（请结合光标前后正文，在光标处生成待插入内容；需与前后文自然衔接，不要重复光标之后已有段落，仅做必要补充、过渡或局部展开。）`
}

export function isEditableInContainer(target: EventTarget | null, container: HTMLElement): boolean {
  if (!(target instanceof Node)) return false
  if (!container.contains(target)) return false
  const el = target instanceof HTMLElement ? target : target.parentElement
  if (!el) return false
  return (
    el.isContentEditable ||
    !!el.closest('[contenteditable="true"]') ||
    !!el.closest('.vditor-ir') ||
    !!el.closest('.w-e-text-container')
  )
}
